import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import {
	MindMapTrait,
	MindMapContentBlock,
	MindMapActionButton,
	MindMapExpandCollapseButton,
} from '../../ui/mindMapElements/mindMapElem';
import { FullscreenToggleButton, PlusButton, MinusButton, CenterMapButton } from '../../ui/buttons/Button';
import { useColorContext } from '../../../context/colorContext';

// Drag momentum tuning (feel free to tweak these values).
const MOMENTUM_START_SPEED = 0.05; // px/ms; minimum release speed to start inertia.
const MOMENTUM_STOP_SPEED = 0.01; // px/ms; inertia stops under this speed.
const MOMENTUM_DAMPING_PER_60FPS_FRAME = 0.92; // lower = stronger friction.

const resolveColor = (preferred, fallback) => {
	const candidate = String(preferred || '').trim();
	if (candidate.length > 0) return candidate;
	return String(fallback || '').trim();
};

const MindMap = ({
	data = {
		id: 'root',
		type: 'text',
		content: 'Mind Map',
		isExpanded: true,
		children: [],
	},
	onDataChange,
	onButtonAction,
	// Layout props
	startX = 100,
	startY = 100,
	horizontalSpacing = 150,
	verticalSpacing = 80,
	groupSpacing = 40, // Extra spacing between sibling groups from different parents
	blockMinWidth = 120,
	blockMaxWidth = 960,
	blockPadding = '10px 14px',
	blockMinHeight = 44,
	// Styling props
	blockBackgroundColor = '#1E1E1E',
	blockBorderColor = '#2A2A2A',
	blockTextColor = '#F5F5F5',
	blockFontSize = 18.67,
	blockFontWeight = 500,
	actionButtonBackgroundColor = '#1E1E1E',
	actionButtonHoverBackgroundColor = '#2A2A2A',
	actionButtonActiveBackgroundColor = '#1A1A1A',
	actionButtonBorderColor = '#2A2A2A',
	actionButtonTextColor = '#F5F5F5',
	// Trait props
	traitColor = 'rgba(109, 40, 217, 0.5)',
	traitOpacity = 1,
	traitStrokeWidth = 1,
	traitCurvature = 0.5,
	// Expand/Collapse button props
	expandButtonSize = 30,
	expandButtonBackgroundColor = '#1E1E1E',
	expandButtonHoverBackgroundColor = '#2A2A2A',
	// Container/Zoom props
	containerWidth = '100%',
	containerHeight = '600px',
	zoomMin = 0.3,
	zoomMax = 3,
	zoomSpeed = 0.1,
	fullscreenButtonColor = '#6D28D9',
	fullscreenButtonSize = 'md',
	zoomButtonColor = '#6D28D9',
	zoomButtonSize = 'md',
	canvasBackground = '#EEF2F8',
	canvasBackdropColor = '#181C22',
	canvasBorder = 'transparent',
	canvasShadow = 'none',
}) => {
	const { bgPrimary, bgSecondary, bgTertiary, textPrimary, activeAccent } = useColorContext();

	const resolvedBlockBackgroundColor = resolveColor(blockBackgroundColor, resolveColor(bgSecondary, '#1E1E1E'));
	const resolvedBlockBorderColor = resolveColor(blockBorderColor, 'transparent');
	const resolvedBlockTextColor = resolveColor(blockTextColor, resolveColor(textPrimary, '#111827'));
	const resolvedActionButtonBackgroundColor = resolveColor(actionButtonBackgroundColor, resolvedBlockBackgroundColor);
	const resolvedActionButtonTextColor = resolveColor(actionButtonTextColor, resolvedBlockTextColor);
	const resolvedTraitColor = resolveColor(traitColor, resolveColor(activeAccent, 'rgba(109, 40, 217, 0.5)'));
	const resolvedFullscreenButtonColor = resolveColor(fullscreenButtonColor, resolveColor(activeAccent, '#6D28D9'));
	const resolvedZoomButtonColor = resolveColor(zoomButtonColor, resolvedFullscreenButtonColor);
	const resolvedCanvasBackground = resolveColor(canvasBackground, resolveColor(bgPrimary, '#111111'));
	const resolvedCanvasBackdropColor = resolveColor(canvasBackdropColor, resolveColor(bgPrimary, '#111111'));
	const resolvedExpandButtonBackgroundColor = resolveColor(expandButtonBackgroundColor, resolvedBlockBackgroundColor);
	const resolvedExpandButtonHoverBackgroundColor = resolveColor(expandButtonHoverBackgroundColor, resolvedExpandButtonBackgroundColor);
	const resolvedNodeDepthColor = resolveColor(bgTertiary, resolveColor(bgSecondary, '#1E1E1E'));

	const [mindmapData, setMindmapData] = useState(data);
	const [collapsingNodes, setCollapsingNodes] = useState(new Set());
	const [nodeHeights, setNodeHeights] = useState({}); // Store measured heights
	const [nodeWidths, setNodeWidths] = useState({}); // Store measured widths
	const nodeRefs = useRef({}); // Store refs to node DOM elements
	const [zoom, setZoom] = useState(1); // Zoom level
	const [pan, setPan] = useState({ x: 0, y: 0 }); // Pan position
	const [isDragging, setIsDragging] = useState(false);
	const [isMomentumPanning, setIsMomentumPanning] = useState(false);
	const dragLastPositionRef = useRef({ x: 0, y: 0 });
	const dragVelocityRef = useRef({ vx: 0, vy: 0 }); // px/ms
	const dragLastTimestampRef = useRef(0);
	const wheelLastTimestampRef = useRef(0);
	const momentumAnimationRef = useRef(null);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const containerRef = useRef(null);
	const layoutCacheRef = useRef(new WeakMap());

	const colorToRgba = useCallback((color, alpha, fallback = 'rgba(109, 40, 217, 1)') => {
		const value = String(color || '').trim();

		const hexMatch = value.match(/^#([a-fA-F0-9]{6})$/);
		if (hexMatch) {
			const hex = hexMatch[1];
			const r = parseInt(hex.slice(0, 2), 16);
			const g = parseInt(hex.slice(2, 4), 16);
			const b = parseInt(hex.slice(4, 6), 16);
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		}

		const rgbaMatch = value.match(/^rgba?\(([^)]+)\)$/i);
		if (rgbaMatch) {
			const parts = rgbaMatch[1].split(',').map((part) => Number(part.trim()));
			if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
				return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
			}
		}

		const fb = String(fallback || '').match(/^rgba?\(([^)]+)\)$/i);
		if (fb) {
			const parts = fb[1].split(',').map((part) => Number(part.trim()));
			if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
				return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
			}
		}

		return `rgba(109, 40, 217, ${alpha})`;
	}, []);

	const colorToRgb = useCallback((color, fallback = '#050505') => {
		const value = String(color || fallback || '').trim();

		const hexMatch = value.match(/^#([a-fA-F0-9]{6})$/);
		if (hexMatch) {
			const hex = hexMatch[1];
			return {
				r: parseInt(hex.slice(0, 2), 16),
				g: parseInt(hex.slice(2, 4), 16),
				b: parseInt(hex.slice(4, 6), 16),
			};
		}

		const shortHexMatch = value.match(/^#([a-fA-F0-9]{3})$/);
		if (shortHexMatch) {
			const hex = shortHexMatch[1];
			return {
				r: parseInt(hex[0] + hex[0], 16),
				g: parseInt(hex[1] + hex[1], 16),
				b: parseInt(hex[2] + hex[2], 16),
			};
		}

		const rgbaMatch = value.match(/^rgba?\(([^)]+)\)$/i);
		if (rgbaMatch) {
			const parts = rgbaMatch[1].split(',').map((part) => Number(part.trim()));
			if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
				return {
					r: parts[0],
					g: parts[1],
					b: parts[2],
				};
			}
		}

		return colorToRgb(fallback, '#050505');
	}, []);

	const mixColors = useCallback((fromColor, toColor, mixRatio, alpha = 1) => {
		const ratio = Math.max(0, Math.min(1, Number(mixRatio) || 0));
		const from = colorToRgb(fromColor, '#050505');
		const to = colorToRgb(toColor, '#6D28D9');
		const red = Math.round(from.r + (to.r - from.r) * ratio);
		const green = Math.round(from.g + (to.g - from.g) * ratio);
		const blue = Math.round(from.b + (to.b - from.b) * ratio);
		return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
	}, [colorToRgb]);

	const resolvedCanvasBase = useMemo(() => {
		const value = String(resolvedCanvasBackground || '').trim();
		if (!value) return '#EEF2F8';
		return value;
	}, [resolvedCanvasBackground]);

	const resolvedCanvasBackdrop = useMemo(() => {
		const value = String(resolvedCanvasBackdropColor || '').trim();
		if (!value) return '#181C22';
		return value;
	}, [resolvedCanvasBackdropColor]);

	const resolvedCanvasBaseForDisplay = useMemo(() => {
		const rgbaMatch = resolvedCanvasBase.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-9]*\.?[0-9]+)\s*\)$/i);
		if (!rgbaMatch) return resolvedCanvasBase;

		const sourceR = Math.max(0, Math.min(255, Number(rgbaMatch[1])));
		const sourceG = Math.max(0, Math.min(255, Number(rgbaMatch[2])));
		const sourceB = Math.max(0, Math.min(255, Number(rgbaMatch[3])));
		const alpha = Math.max(0, Math.min(1, Number(rgbaMatch[4])));

		// Always compose over the same backdrop to keep normal/fullscreen colors identical.
		const matte = colorToRgb(resolvedCanvasBackdrop, '#181C22');
		const composedR = Math.round((sourceR * alpha) + (matte.r * (1 - alpha)));
		const composedG = Math.round((sourceG * alpha) + (matte.g * (1 - alpha)));
		const composedB = Math.round((sourceB * alpha) + (matte.b * (1 - alpha)));

		return `rgb(${composedR}, ${composedG}, ${composedB})`;
	}, [colorToRgb, resolvedCanvasBackdrop, resolvedCanvasBase]);

	const nodeAccentColor = useMemo(() => {
		const primary = String(resolvedFullscreenButtonColor || '').trim();
		if (primary) return primary;
		const secondary = String(resolvedTraitColor || '').trim();
		if (secondary) return secondary;
		return '#6D28D9';
	}, [resolvedFullscreenButtonColor, resolvedTraitColor]);

	const premiumCanvasBackground = useMemo(() => resolvedCanvasBaseForDisplay, [resolvedCanvasBaseForDisplay]);

	const resolveContrastTextColor = useCallback((backgroundColor) => {
		const { r, g, b } = colorToRgb(backgroundColor, '#0F172A');
		const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
		return luminance > 0.62 ? '#0F172A' : '#F8FAFC';
	}, [colorToRgb]);

	const maxNodeDepth = useMemo(() => {
		const traverse = (node, depth = 0) => {
			if (!node) return depth;
			let currentMax = Math.max(0, depth);
			if (Array.isArray(node.children) && node.children.length > 0) {
				node.children.forEach((child) => {
					currentMax = Math.max(currentMax, traverse(child, depth + 1));
				});
			}
			return currentMax;
		};

		return traverse(mindmapData, 0);
	}, [mindmapData]);

	const getDepthAccentRatio = useCallback((depth) => {
		const safeDepth = Math.max(0, Number.isFinite(depth) ? depth : 0);
		if (safeDepth === 0) return 0;
		if (maxNodeDepth <= 0) return 1;

		const normalizedDepth = Math.min(1, safeDepth / maxNodeDepth);
		// 2x softer easing for a very gentle premium progression.
		const easedDepth = Math.pow(normalizedDepth, 1.42);
		return Math.max(0.04, easedDepth);
	}, [maxNodeDepth]);

	const resolveNodeFillColor = useCallback((depth, isButton = false) => {
		return resolvedBlockBackgroundColor;
	}, [resolvedBlockBackgroundColor]);

	const resolveNodeBorderColor = useCallback((depth, isButton = false) => {
		const safeDepth = Math.max(0, Number.isFinite(depth) ? depth : 0);
		if (safeDepth === 0) return 'rgba(255, 255, 255, 0.12)';

		const borderTarget = '#3B424D';
		const depthRatio = getDepthAccentRatio(safeDepth);
		const maxBorderTone = isButton ? 0.24 : 0.28;
		const borderStrength = Math.min(maxBorderTone, 0.06 + depthRatio * 0.26);
		return mixColors(resolvedBlockBorderColor, borderTarget, isButton ? borderStrength * 0.9 : borderStrength, 1);
	}, [getDepthAccentRatio, mixColors, resolvedBlockBorderColor]);

	const resolveNodeHoverColor = useCallback((depth) => {
		return resolvedBlockBackgroundColor;
	}, [resolvedBlockBackgroundColor]);

	const resolveNodeActiveColor = useCallback((depth) => {
		return resolvedBlockBackgroundColor;
	}, [resolvedBlockBackgroundColor]);

	const premiumNodeShadow = useMemo(() => {
		return 'none';
	}, [colorToRgba]);

	const expandButtonStyle = useMemo(() => {
		const resolvedBackground = String(resolvedBlockBackgroundColor || '#1E1E1E').trim();
		const resolvedHoverBackground = String(resolvedBlockBackgroundColor || '#1E1E1E').trim();

		return {
			background: resolvedBackground,
			hoverBackground: resolvedHoverBackground,
			border: 'transparent',
			icon: resolveContrastTextColor(resolvedBackground),
			focusRing: colorToRgba(nodeAccentColor, 0.32, nodeAccentColor),
			shadow: 'none',
		};
	}, [
		resolvedBlockBackgroundColor,
		resolveContrastTextColor,
		colorToRgba,
		nodeAccentColor,
	]);

	const nodeSurfaceStyles = useMemo(() => {
		const contentBase = String(resolvedBlockBackgroundColor || '#1E1E1E').trim();
		const actionBase = String(resolvedActionButtonBackgroundColor || '#1E1E1E').trim();

		return {
			content: {
				backgroundImage: 'none',
				borderColor: 'transparent',
				borderRadius: 18,
				backdropFilter: 'none',
				WebkitBackdropFilter: 'none',
				letterSpacing: '0.01em',
				lineHeight: 1.42,
				transition: 'transform 0.24s cubic-bezier(0.23, 1, 0.32, 1), background-color 0.24s ease, background 0.24s ease',
			},
			action: {
				backgroundImage: 'none',
				borderColor: 'transparent',
				borderRadius: 16,
				backdropFilter: 'none',
				WebkitBackdropFilter: 'none',
				letterSpacing: '0.012em',
				lineHeight: 1.38,
				transition: 'transform 0.22s cubic-bezier(0.23, 1, 0.32, 1), background-color 0.22s ease',
			},
		};
	}, [
		resolvedActionButtonBackgroundColor,
		resolvedBlockBackgroundColor,
	]);

	// Invalidate memoized subtree layouts when measured sizes or expansion state change.
	// Without this, a parent can keep stale child Y positions until a manual expand/collapse.
	useEffect(() => {
		layoutCacheRef.current = new WeakMap();
	}, [mindmapData, nodeHeights, nodeWidths, collapsingNodes, startY, blockMinHeight, blockMinWidth]);

	const stopMomentumPan = useCallback(() => {
		if (momentumAnimationRef.current) {
			cancelAnimationFrame(momentumAnimationRef.current);
			momentumAnimationRef.current = null;
		}
		setIsMomentumPanning(false);
		dragVelocityRef.current = { vx: 0, vy: 0 };
	}, []);

	const applyCenteredZoom = useCallback((zoomDelta) => {
		const container = containerRef.current;
		if (!container) return;

		const centerX = container.clientWidth / 2;
		const centerY = container.clientHeight / 2;

		setZoom((previousZoom) => {
			const clampedZoom = Math.max(zoomMin, Math.min(zoomMax, previousZoom + zoomDelta));
			if (clampedZoom === previousZoom) return previousZoom;

			setPan((previousPan) => {
				const scale = clampedZoom / previousZoom;
				return {
					x: centerX - scale * (centerX - previousPan.x),
					y: centerY - scale * (centerY - previousPan.y),
				};
			});

			return clampedZoom;
		});
	}, [zoomMin, zoomMax]);

	const areButtonActionsEqual = (left, right) => {
		if (left === right) return true;
		if (!left || !right) return false;
		return left.actionType === right.actionType && left.target === right.target;
	};

	const mergeNodeWithStructuralSharing = (previousNode, incomingNode) => {
		if (!incomingNode) return incomingNode;
		if (!previousNode) return incomingNode;
		if (previousNode.id !== incomingNode.id) return incomingNode;

		const incomingChildren = Array.isArray(incomingNode.children) ? incomingNode.children : [];
		const previousChildren = Array.isArray(previousNode.children) ? previousNode.children : [];
		const previousChildrenById = new Map(previousChildren.map((child) => [child.id, child]));

		const mergedChildren = incomingChildren.map((incomingChild) => {
			const matchingPreviousChild = previousChildrenById.get(incomingChild.id);
			return mergeNodeWithStructuralSharing(matchingPreviousChild, incomingChild);
		});

		const previousHasSameChildrenLength = previousChildren.length === mergedChildren.length;
		const childrenUnchanged = previousHasSameChildrenLength
			&& previousChildren.every((child, index) => child === mergedChildren[index]);

		const sameCoreFields = (
			previousNode.type === incomingNode.type
			&& previousNode.content === incomingNode.content
			&& previousNode.isExpanded === incomingNode.isExpanded
			&& areButtonActionsEqual(previousNode.buttonAction, incomingNode.buttonAction)
		);

		if (sameCoreFields && childrenUnchanged) {
			return previousNode;
		}

		return {
			...incomingNode,
			children: mergedChildren,
		};
	};

	useEffect(() => {
		setMindmapData((previousData) => mergeNodeWithStructuralSharing(previousData, data));
	}, [data]);

	// Handle pan with mouse drag
	const handleMouseDown = (e) => {
		if (e.button !== 0) return;
		if (e.target.closest('[data-mindmap-interactive="true"], button, [role="button"]')) return;
		stopMomentumPan();
		setIsDragging(true);
		dragLastPositionRef.current = { x: e.clientX, y: e.clientY };
		dragLastTimestampRef.current = performance.now();
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;
		const now = performance.now();
		const previous = dragLastPositionRef.current;
		const dx = e.clientX - previous.x;
		const dy = e.clientY - previous.y;
		const dt = Math.max(1, now - (dragLastTimestampRef.current || now));

		if (!dx && !dy) return;

		setPan((prev) => ({
			x: prev.x + dx,
			y: prev.y + dy,
		}));

		dragVelocityRef.current = {
			vx: dx / dt,
			vy: dy / dt,
		};

		dragLastPositionRef.current = { x: e.clientX, y: e.clientY };
		dragLastTimestampRef.current = now;
	};

	const handleMouseUp = () => {
		if (isDragging) {
			const startVelocity = dragVelocityRef.current;
			const initialSpeed = Math.hypot(startVelocity.vx, startVelocity.vy);

			if (initialSpeed > MOMENTUM_START_SPEED) {
				setIsMomentumPanning(true);
				let vx = startVelocity.vx;
				let vy = startVelocity.vy;
				let lastTs = performance.now();

				const step = (ts) => {
					const dt = Math.max(1, ts - lastTs);
					lastTs = ts;

					setPan((prev) => ({
						x: prev.x + vx * dt,
						y: prev.y + vy * dt,
					}));

					const damping = Math.pow(MOMENTUM_DAMPING_PER_60FPS_FRAME, dt / 16.6667);
					vx *= damping;
					vy *= damping;

					if (Math.hypot(vx, vy) <= MOMENTUM_STOP_SPEED) {
						setIsMomentumPanning(false);
						momentumAnimationRef.current = null;
						return;
					}

					momentumAnimationRef.current = requestAnimationFrame(step);
				};

				momentumAnimationRef.current = requestAnimationFrame(step);
			}
		}

		setIsDragging(false);
		dragLastPositionRef.current = { x: 0, y: 0 };
		dragLastTimestampRef.current = 0;
	};

	useEffect(() => () => {
		if (momentumAnimationRef.current) cancelAnimationFrame(momentumAnimationRef.current);
	}, []);

	const handleZoomIn = useCallback((event) => {
		event?.stopPropagation?.();
		applyCenteredZoom(zoomSpeed);
	}, [zoomSpeed, applyCenteredZoom]);

	const handleZoomOut = useCallback((event) => {
		event?.stopPropagation?.();
		applyCenteredZoom(-zoomSpeed);
	}, [zoomSpeed, applyCenteredZoom]);

	// Add wheel event listener with passive: false to allow preventDefault
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const WHEEL_STEP_INTERVAL_MS = 80;

		const wheelHandler = (e) => {
			e.preventDefault();
			e.stopPropagation();

			const now = performance.now();
			if (now - wheelLastTimestampRef.current < WHEEL_STEP_INTERVAL_MS) {
				return;
			}
			wheelLastTimestampRef.current = now;

			if (e.deltaY > 0) {
				handleZoomOut();
				return;
			}
			handleZoomIn();
		};

		container.addEventListener('wheel', wheelHandler, { passive: false });

		return () => {
			container.removeEventListener('wheel', wheelHandler);
		};
	}, [handleZoomIn, handleZoomOut]);

	useEffect(() => {
		if (!isFullscreen) return undefined;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isFullscreen]);

	useEffect(() => {
		const onFullscreenChange = () => {
			const currentElement = document.fullscreenElement || document.webkitFullscreenElement || null;
			setIsFullscreen(currentElement === containerRef.current);
		};

		document.addEventListener('fullscreenchange', onFullscreenChange);
		document.addEventListener('webkitfullscreenchange', onFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', onFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
		};
	}, []);

	const toggleFullscreen = useCallback(async () => {
		const container = containerRef.current;
		if (!container) return;

		try {
			const currentElement = document.fullscreenElement || document.webkitFullscreenElement || null;

			if (currentElement === container) {
				if (document.exitFullscreen) {
					await document.exitFullscreen();
					return;
				}
				if (document.webkitExitFullscreen) {
					document.webkitExitFullscreen();
					return;
				}
				setIsFullscreen(false);
				return;
			}

			if (container.requestFullscreen) {
				await container.requestFullscreen();
				return;
			}
			if (container.webkitRequestFullscreen) {
				container.webkitRequestFullscreen();
				return;
			}

			// Fallback for environments without Fullscreen API.
			setIsFullscreen((prev) => !prev);
		} catch (_error) {
			// Safety fallback: keep legacy behavior if native fullscreen fails.
			setIsFullscreen((prev) => !prev);
		}
	}, []);
	const toggleExpanded = useCallback((nodeRenderKey) => {
		if (typeof nodeRenderKey !== 'string' || !nodeRenderKey.length) return;

		const pathParts = nodeRenderKey
			.split('.')
			.map((value) => Number.parseInt(value, 10))
			.filter((value) => Number.isInteger(value));

		if (pathParts.length === 0 || pathParts[0] !== 0) return;

		const getNodeByPath = (rootNode) => {
			let current = rootNode;
			for (let depth = 1; depth < pathParts.length; depth += 1) {
				const childIndex = pathParts[depth];
				if (!current?.children || childIndex < 0 || childIndex >= current.children.length) {
					return null;
				}
				current = current.children[childIndex];
			}
			return current;
		};

		const updateNodeByPath = (node, depth = 1, expandedValue = true) => {
			if (depth === pathParts.length) {
				return { ...node, isExpanded: expandedValue };
			}

			const childIndex = pathParts[depth];
			if (!node?.children || childIndex < 0 || childIndex >= node.children.length) {
				return node;
			}

			return {
				...node,
				children: node.children.map((child, index) => (
					index === childIndex ? updateNodeByPath(child, depth + 1, expandedValue) : child
				)),
			};
		};

		const targetNode = getNodeByPath(mindmapData);
		if (!targetNode) return;

		const isCurrentlyExpanded = targetNode.isExpanded !== false;

		if (isCurrentlyExpanded) {
			setCollapsingNodes((prev) => new Set(prev).add(nodeRenderKey));

			const newData = updateNodeByPath(mindmapData, 1, false);
			setMindmapData(newData);
			onDataChange?.(newData);

			setTimeout(() => {
				setCollapsingNodes((prev) => {
					const next = new Set(prev);
					next.delete(nodeRenderKey);
					return next;
				});
			}, 200);
			return;
		}

		const newData = updateNodeByPath(mindmapData, 1, true);
		setMindmapData(newData);
		onDataChange?.(newData);
	}, [mindmapData, onDataChange]);

	const measureNodeSizes = useCallback(() => {
		const measuredHeights = {};
		const measuredWidths = {};
		Object.entries(nodeRefs.current).forEach(([nodeId, ref]) => {
			if (ref) {
				measuredHeights[nodeId] = ref.offsetHeight;
				measuredWidths[nodeId] = ref.offsetWidth;
			}
		});

		setNodeHeights((previousHeights) => {
			const previousKeys = Object.keys(previousHeights);
			const nextKeys = Object.keys(measuredHeights);

			if (previousKeys.length !== nextKeys.length) {
				return measuredHeights;
			}

			const hasChanges = nextKeys.some((key) => previousHeights[key] !== measuredHeights[key]);
			return hasChanges ? measuredHeights : previousHeights;
		});

		setNodeWidths((previousWidths) => {
			const previousKeys = Object.keys(previousWidths);
			const nextKeys = Object.keys(measuredWidths);

			if (previousKeys.length !== nextKeys.length) {
				return measuredWidths;
			}

			const hasChanges = nextKeys.some((key) => previousWidths[key] !== measuredWidths[key]);
			return hasChanges ? measuredWidths : previousWidths;
		});
	}, []);

	// Measure in layout phase to avoid initial overlap flicker
	useLayoutEffect(() => {
		measureNodeSizes();
	}, [mindmapData, collapsingNodes, isFullscreen, measureNodeSizes]);

	// Keep measurements fresh when fonts load, viewport changes, or block content reflows
	useEffect(() => {
		const nodeElements = Object.values(nodeRefs.current).filter(Boolean);
		if (nodeElements.length === 0) return undefined;

		const rafId = requestAnimationFrame(() => {
			measureNodeSizes();
		});

		const onResize = () => {
			measureNodeSizes();
		};
		window.addEventListener('resize', onResize);

		let resizeObserver;
		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => {
				measureNodeSizes();
			});
			nodeElements.forEach((element) => resizeObserver.observe(element));
		}

		let cancelled = false;
		if (document.fonts?.ready) {
			document.fonts.ready.then(() => {
				if (!cancelled) {
					measureNodeSizes();
				}
			});
		}

		return () => {
			cancelled = true;
			cancelAnimationFrame(rafId);
			window.removeEventListener('resize', onResize);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [mindmapData, collapsingNodes, isFullscreen, measureNodeSizes]);

	const containsDisplayMath = (content) => {
		const text = String(content || '');
		return /\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]/.test(text);
	};

	const isFormulaOnlyContent = (content) => {
		const text = String(content || '').trim();
		if (!text) return false;
		return (
			/^\$\$[\s\S]+\$\$$/.test(text)
			|| /^\$[^\n$]+\$$/.test(text)
			|| /^\\\([\s\S]+\\\)$/.test(text)
			|| /^\\\[[\s\S]+\\\]$/.test(text)
		);
	};

	const getEstimatedWidthCap = useCallback((content) => {
		const isFormulaOnly = isFormulaOnlyContent(content);
		const hasDisplay = containsDisplayMath(content);

		if (isFormulaOnly) {
			return Math.max(blockMaxWidth, 1400);
		}

		if (hasDisplay) {
			return Math.max(blockMaxWidth, 1250);
		}

		return blockMaxWidth;
	}, [blockMaxWidth]);

	const getNodeLayoutWidth = useCallback((content, measuredWidth) => {
		if (Number.isFinite(measuredWidth) && measuredWidth > 0) {
			// Final sizing should follow the real rendered formula width.
			return Math.max(blockMinWidth, measuredWidth);
		}

		const widthCap = getEstimatedWidthCap(content);
		return Math.max(blockMinWidth, Math.min(estimateNodeWidth(content), widthCap));
	}, [blockMinWidth, getEstimatedWidthCap]);

	const buildDepthOffsets = useCallback((maxWidthsByDepth = {}) => {
		const offsets = { 0: 0 };
		const depthKeys = Object.keys(maxWidthsByDepth)
			.map((key) => Number(key))
			.filter((value) => Number.isInteger(value) && value >= 0);

		const maxDepth = depthKeys.length ? Math.max(...depthKeys) : 0;
		const gapToButton = 12;
		const gapToNextLevel = Math.max(30, horizontalSpacing * 0.4);

		for (let depth = 1; depth <= maxDepth + 1; depth += 1) {
			const previousWidth = Number(maxWidthsByDepth[depth - 1] || blockMinWidth);
			offsets[depth] = offsets[depth - 1] + previousWidth + gapToButton + expandButtonSize + gapToNextLevel;
		}

		return offsets;
	}, [horizontalSpacing, blockMinWidth, expandButtonSize]);

	// Calculate maximum width at each depth level (independent of expansion state)
	const getMaxWidthAtDepth = useCallback((node, depth = 0, maxWidths = {}, nodePath = '0') => {
		const measuredWidth = nodeWidths[nodePath];
		const nodeWidth = getNodeLayoutWidth(node.content, measuredWidth);
		maxWidths[depth] = Math.max(maxWidths[depth] || 0, nodeWidth);
		
		// Traverse all children regardless of expansion state
		// This ensures consistent width calculations even when toggling expand/collapse
		if (node.children && node.children.length > 0) {
			node.children.forEach((child, index) => {
				getMaxWidthAtDepth(child, depth + 1, maxWidths, `${nodePath}.${index}`);
			});
		}
		
		return maxWidths;
	}, [nodeWidths, getNodeLayoutWidth]);

	// Estimate node width based on content
	const estimateNodeWidth = (content) => {
		// Approximate: ~9 pixels per character for Inter font at 14px
		// This accounts for average character width in the Inter font family
		const isFormulaOnly = isFormulaOnlyContent(content);
		const hasDisplay = containsDisplayMath(content);
		const charWidth = isFormulaOnly ? 10 : hasDisplay ? 9.5 : 9;
		const paddingWidth = 28; // 14px left + 14px right (from '10px 14px')
		const borderWidth = 2; // 1px left + 1px right border
		// With box-sizing: border-box, width includes content + padding + border
		const estimatedTotalWidth = content.length * charWidth + paddingWidth + borderWidth;
		const maxWidthForNode = isFormulaOnly ? Math.max(blockMaxWidth, 1400) : hasDisplay ? Math.max(blockMaxWidth, 1250) : blockMaxWidth;
		return Math.max(blockMinWidth, Math.min(estimatedTotalWidth, maxWidthForNode));
	};

	// Calculate the total height required for a subtree (for horizontal layout)
	// Use measured heights if available, otherwise use minimum height
	const calculateSubtreeHeight = (node, nodePath = '0') => {
		if (!node.children || node.children.length === 0 || !node.isExpanded) {
			// Use measured height if available, else minimum
			return nodeHeights[nodePath] || blockMinHeight;
		}
		
		// Sum of all children heights plus spacing between them
		let totalChildrenHeight = 0;
		node.children.forEach((child, index) => {
			const childPath = `${nodePath}.${index}`;
			totalChildrenHeight += calculateSubtreeHeight(child, childPath);
			if (index < node.children.length - 1) {
				// Small spacing between siblings from same parent
				totalChildrenHeight += 15; // Compact spacing within a group
			}
		});
		
		// Add group spacing (padding around this parent's children group)
		// This creates visual separation between different parent groups
		totalChildrenHeight += groupSpacing;
		
		// The subtree height is the maximum of parent height and children total height
		const nodeHeight = nodeHeights[nodePath] || blockMinHeight;
		return Math.max(nodeHeight, totalChildrenHeight);
	};

	// Calculate positions for all nodes using horizontal tree layout
	// All nodes at the same depth level will have the same X position
	const calculatePositions = (node, depth = 0, centerY = startY, parentCollapsing = false, maxWidthsAtDepth = {}, depthOffsets = { 0: 0 }, nodePath = '0') => {
		const nodeHeight = nodeHeights[nodePath] || blockMinHeight;
		const maxWidthAtCurrentDepth = maxWidthsAtDepth[depth] || blockMinWidth;
		const isNodeExpanded = node.isExpanded !== false;
		const isNodeCollapsing = collapsingNodes.has(nodePath);
		const measuredNodeWidth = nodeWidths[nodePath] || 0;
		const nodeSignature = `${node.type || 'text'}|${node.content}|${node.children?.length || 0}|${isNodeExpanded ? 1 : 0}|${node.buttonAction?.actionType || ''}|${node.buttonAction?.target || ''}`;
		const cacheKey = `${nodePath}|${depth}|${centerY}|${parentCollapsing ? 1 : 0}|${nodeHeight}|${measuredNodeWidth}|${maxWidthAtCurrentDepth}|${isNodeCollapsing ? 1 : 0}|${nodeSignature}`;

		let nodeCache = layoutCacheRef.current.get(node);
		if (!nodeCache) {
			nodeCache = new Map();
			layoutCacheRef.current.set(node, nodeCache);
		}

		const cachedLayout = nodeCache.get(cacheKey);
		if (cachedLayout) {
			return cachedLayout;
		}

		// Use measured height if available, otherwise use minimum
		const estimatedNodeHeight = nodeHeight;
		const estimatedNodeWidth = getNodeLayoutWidth(node.content, measuredNodeWidth);
		const hasDisplayMath = containsDisplayMath(node.content);
		const isFormulaOnly = isFormulaOnlyContent(node.content);

		// Check if this node is collapsing
		const isNodeCollapsingForTree = isNodeCollapsing;
		
		// Propagate collapsing state to all descendants
		const shouldPropagateCollapsing = parentCollapsing || isNodeCollapsingForTree;

		// Calculate X using cumulative depth offsets so children always stay to the right.
		const nodeX = startX + Number(depthOffsets[depth] || 0);
		const nodeY = centerY - estimatedNodeHeight / 2;

		// Calculate expandButton position (to the right of the node, centered vertically)
		// Use consistent 12px gap between node and button
		const expandButtonX = nodeX + estimatedNodeWidth + 12;
		const expandButtonY = centerY - expandButtonSize / 2;

		const nodeInfo = {
			id: node.id,
			renderKey: nodePath,
			type: node.type || 'text',
			content: node.content,
			position: { x: nodeX, y: nodeY },
			hasChildren: node.children && node.children.length > 0,
			isExpanded: node.isExpanded !== false,
			buttonAction: node.buttonAction,
			estimatedHeight: estimatedNodeHeight,
			estimatedWidth: estimatedNodeWidth,
			hasDisplayMath,
			isFormulaOnly,
			expandButtonX: expandButtonX,
			expandButtonY: expandButtonY,
			centerY: centerY, // Store center for trait calculations
			depth: depth,
			isCollapsing: parentCollapsing, // Only children animate, not the parent node itself
		};

		const result = {
			nodes: [nodeInfo],
			traits: [],
			maxX: nodeX + estimatedNodeWidth,
		};

		// If node has children and is expanded, position them to the right
		// Also render children during collapse animation (shouldPropagateCollapsing)
		if (node.children && node.children.length > 0 && (node.isExpanded !== false || shouldPropagateCollapsing)) {
			// Calculate total height needed for all children
			let totalChildrenHeight = 0;
			const childHeights = node.children.map((child, childIndex) => {
				const childPath = `${nodePath}.${childIndex}`;
				const height = calculateSubtreeHeight(child, childPath);
				totalChildrenHeight += height;
				return height;
			});
			
			// Add spacing between children (small spacing within same parent group)
			totalChildrenHeight += (node.children.length - 1) * 15; // Compact spacing

			// Calculate the actual center Y of the parent based on its real measured height
			// This ensures children are positioned correctly even when parent has grown due to long content
			const actualParentCenterY = nodeY + estimatedNodeHeight / 2;

			// Starting Y position (center the entire group of children around parent's actual center)
			// First child should be at: actualParentCenterY - (totalHeight / 2) + (firstChildHeight / 2)
			let currentChildCenterY = actualParentCenterY - totalChildrenHeight / 2 + childHeights[0] / 2;
			
			// Children are at depth + 1
			const childDepth = depth + 1;

				node.children.forEach((child, index) => {
				const childHeight = childHeights[index];
				const childPath = `${nodePath}.${index}`;
				const childResult = calculatePositions(child, childDepth, currentChildCenterY, shouldPropagateCollapsing, maxWidthsAtDepth, depthOffsets, childPath);
				
				// Add trait from expand button to child node
				const childInfo = childResult.nodes[0];
				
			if (!isNodeCollapsingForTree && !shouldPropagateCollapsing && node.isExpanded !== false) {
				// Start point: center right of expand button
				const startX = expandButtonX + expandButtonSize;
				const startY = expandButtonY + expandButtonSize / 2;
				
				// End point: left edge of child node, centered vertically on the actual measured height
				const childActualHeight = nodeHeights[childInfo.renderKey] || blockMinHeight;
				const endX = childInfo.position.x;
				const endY = childInfo.position.y + childActualHeight / 2;
				
				result.traits.push({
					id: `trait-${nodePath}-${childInfo.renderKey}`,
					parentId: node.id,
					childId: child.id,
					parentRenderKey: nodePath,
					childRenderKey: childInfo.renderKey,
					start: {
						x: startX,
						y: startY,
					},
					end: {
						x: endX,
						y: endY,
					},
					isCollapsing: false,
				});
			}

			// Add child nodes
			result.nodes.push(...childResult.nodes);
			
			// Add child traits only if not in collapse animation
			if (!shouldPropagateCollapsing) {
				result.traits.push(...childResult.traits);
			}

			// Update Y for next sibling (small spacing within group)
			currentChildCenterY += childHeight / 2 + 15 + (index < node.children.length - 1 ? childHeights[index + 1] / 2 : 0);

			// Track maximum X
			result.maxX = Math.max(result.maxX, childResult.maxX);
			});
		}

		nodeCache.set(cacheKey, result);
		return result;
	};

	// Calculate max widths at each depth level first
	const maxWidthsAtDepth = useMemo(() => getMaxWidthAtDepth(mindmapData, 0, {}, '0'), [getMaxWidthAtDepth, mindmapData]);
	const depthOffsets = useMemo(() => buildDepthOffsets(maxWidthsAtDepth), [buildDepthOffsets, maxWidthsAtDepth]);
	
	// Now calculate positions using max widths
	const layoutData = useMemo(
		() => calculatePositions(mindmapData, 0, startY, false, maxWidthsAtDepth, depthOffsets),
		[mindmapData, startY, maxWidthsAtDepth, depthOffsets, nodeHeights, collapsingNodes, blockMinHeight, blockMinWidth]
	);

	// Filter traits to only show those connecting visible nodes
	// A trait should be visible if both its start and end nodes are visible (not collapsed and not animating collapse)
	const getVisibleNodeKeys = useCallback(() => {
		const visibleKeys = new Set();
		const traverse = (node, nodePath = '0') => {
			visibleKeys.add(nodePath);
			// Only traverse children if node is expanded AND not currently collapsing
			if (node.isExpanded !== false && !collapsingNodes.has(nodePath)) {
				if (node.children) {
					node.children.forEach((child, index) => traverse(child, `${nodePath}.${index}`));
				}
			}
		};
		traverse(mindmapData);
		return visibleKeys;
	}, [mindmapData, collapsingNodes]);

	const visibleNodeKeys = useMemo(() => getVisibleNodeKeys(), [getVisibleNodeKeys]);
	
	// Filter traits to only include those where BOTH nodes are visible
	const filteredTraits = useMemo(() => layoutData.traits.filter((trait) => {
		if (trait.parentRenderKey && trait.childRenderKey) {
			return visibleNodeKeys.has(trait.parentRenderKey) && visibleNodeKeys.has(trait.childRenderKey);
		}

		const parentId = trait.parentId;
		const childId = trait.childId;

		if (parentId && childId) {
			return true;
		}

		const parts = trait.id.split('-');
		if (parts.length < 3) return true;
		const fallbackParentId = parts.slice(1, -1).join('-');
		const fallbackChildId = parts[parts.length - 1];
		return visibleNodeKeys.has(fallbackParentId) && visibleNodeKeys.has(fallbackChildId);
	}), [layoutData.traits, visibleNodeKeys]);
	
	// Create filtered layout data
	const filteredLayoutData = useMemo(() => ({
		...layoutData,
		traits: filteredTraits,
	}), [layoutData, filteredTraits]);

	const handleCenterMap = useCallback((event) => {
		event?.stopPropagation?.();

		const container = containerRef.current;
		if (!container || !Array.isArray(layoutData.nodes) || layoutData.nodes.length === 0) {
			setPan({ x: 0, y: 0 });
			return;
		}

		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;

		for (const nodeInfo of layoutData.nodes) {
			const width = Number(nodeInfo?.estimatedWidth || 0);
			const height = Number(nodeInfo?.estimatedHeight || 0);
			const x = Number(nodeInfo?.position?.x || 0);
			const y = Number(nodeInfo?.position?.y || 0);

			minX = Math.min(minX, x);
			minY = Math.min(minY, y);
			maxX = Math.max(maxX, x + width);
			maxY = Math.max(maxY, y + height);
		}

		if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
			setPan({ x: 0, y: 0 });
			return;
		}

		const contentCenterX = (minX + maxX) / 2;
		const contentCenterY = (minY + maxY) / 2;
		const viewportPadding = 40;
		const viewportCenterX = (container.clientWidth - viewportPadding * 2) / 2;
		const viewportCenterY = (container.clientHeight - viewportPadding * 2) / 2;

		setPan({
			x: viewportCenterX - (contentCenterX * zoom),
			y: viewportCenterY - (contentCenterY * zoom),
		});
	}, [layoutData.nodes, zoom]);

	// Render all nodes
	const renderNodes = () => {
		return layoutData.nodes.map((nodeInfo) => {
			const isButton = nodeInfo.type === 'button';
			const hasButtonAction = Boolean(nodeInfo.buttonAction);
			const NodeComponent = isButton ? MindMapActionButton : MindMapContentBlock;
			const safeDepth = Number.isFinite(nodeInfo.depth) ? Math.max(0, nodeInfo.depth) : 0;
			const depthEmphasis = Math.max(0.18, 1 - Math.min(safeDepth, 5) * 0.14);
			const borderStrength = 0.26 + depthEmphasis * 0.34 + (safeDepth === 0 ? 0.14 : 0);
			const glowStrength = 0.1 + depthEmphasis * 0.16 + (hasButtonAction ? 0.06 : 0);
			const outerHaloStrength = 0.08 + depthEmphasis * 0.08;
			const innerLightStrength = 0.04 + depthEmphasis * 0.05;
			const nodeShadow = 'none';

			// Only apply animation when node is actually collapsing/disappearing
			const animation = nodeInfo.isCollapsing 
				? 'mindMapFadeIn 0.2s ease-out reverse forwards'
				: undefined;

			const commonProps = {
				key: nodeInfo.renderKey,
				text: nodeInfo.content,
				position: nodeInfo.position,
				autoSize: false,
				width: nodeInfo.estimatedWidth,
			minHeight: blockMinHeight,
			maxHeight: undefined,
			minWidth: blockMinWidth,
			maxWidth: nodeInfo.isFormulaOnly ? undefined : nodeInfo.hasDisplayMath ? Math.max(blockMaxWidth, 1600) : blockMaxWidth,
			padding: blockPadding,
			borderWidth: 0,
			backgroundColor: resolveNodeFillColor(safeDepth, isButton),
			borderColor: 'transparent',
			textColor: isButton ? resolvedActionButtonTextColor : resolvedBlockTextColor,
			fontSize: blockFontSize,
			fontWeight: blockFontWeight,
			ref: (el) => {
					if (el) {
						nodeRefs.current[nodeInfo.renderKey] = el;
					} else {
						delete nodeRefs.current[nodeInfo.renderKey];
					}
			},
			style: {
				boxSizing: 'border-box',
				boxShadow: 'none',
				...(isButton ? nodeSurfaceStyles.action : nodeSurfaceStyles.content),
				...(animation ? { animation } : {}),
					cursor: hasButtonAction ? 'pointer' : undefined,
			},
		};

			const handleNodeAction = () => {
				if (!nodeInfo.buttonAction) return;
				onButtonAction?.(nodeInfo.buttonAction, nodeInfo.id);
			};

		if (isButton) {
			return (
				<NodeComponent
					{...commonProps}
					hoverBackgroundColor={resolveNodeHoverColor(safeDepth)}
					activeBackgroundColor={resolveNodeActiveColor(safeDepth)}
						onClick={handleNodeAction}
				/>
			);
		}

			return (
				<NodeComponent
					{...commonProps}
					onClick={hasButtonAction ? handleNodeAction : undefined}
					onMouseDown={hasButtonAction ? (event) => event.stopPropagation() : undefined}
					onKeyDown={
						hasButtonAction
							? (event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault();
									handleNodeAction();
								}
							}
							: undefined
					}
					role={hasButtonAction ? 'button' : undefined}
					tabIndex={hasButtonAction ? 0 : undefined}
					data-mindmap-interactive={hasButtonAction ? 'true' : undefined}
				/>
			);
	});
};

// Touch handlers for mobile panning
const handleTouchStart = (e) => {
	if (!e || !e.touches || e.touches.length === 0) return;
	// Ignore when interacting with buttons/interactive elements
	if (e.target.closest && e.target.closest('[data-mindmap-interactive="true"], button, [role="button"]')) return;
	e.preventDefault?.();
	stopMomentumPan();
	setIsDragging(true);
	const t = e.touches[0];
	dragLastPositionRef.current = { x: t.clientX, y: t.clientY };
	dragLastTimestampRef.current = performance.now();
};

const handleTouchMove = (e) => {
	if (!isDragging) return;
	if (!e || !e.touches || e.touches.length === 0) return;
	e.preventDefault?.();
	const t = e.touches[0];
	const now = performance.now();
	const previous = dragLastPositionRef.current;
	const dx = t.clientX - previous.x;
	const dy = t.clientY - previous.y;
	const dt = Math.max(1, now - (dragLastTimestampRef.current || now));
	if (!dx && !dy) return;
	setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
	dragVelocityRef.current = { vx: dx / dt, vy: dy / dt };
	dragLastPositionRef.current = { x: t.clientX, y: t.clientY };
	dragLastTimestampRef.current = now;
};

const handleTouchEnd = (e) => {
	if (isDragging) {
		const startVelocity = dragVelocityRef.current;
		const initialSpeed = Math.hypot(startVelocity.vx, startVelocity.vy);
		if (initialSpeed > MOMENTUM_START_SPEED) {
			setIsMomentumPanning(true);
			let vx = startVelocity.vx;
			let vy = startVelocity.vy;
			let lastTs = performance.now();
			const step = (ts) => {
				const dt = Math.max(1, ts - lastTs);
				lastTs = ts;
				setPan((prev) => ({ x: prev.x + vx * dt, y: prev.y + vy * dt }));
				const damping = Math.pow(MOMENTUM_DAMPING_PER_60FPS_FRAME, dt / 16.6667);
				vx *= damping; vy *= damping;
				if (Math.hypot(vx, vy) <= MOMENTUM_STOP_SPEED) {
					setIsMomentumPanning(false);
					momentumAnimationRef.current = null;
					return;
				}
				momentumAnimationRef.current = requestAnimationFrame(step);
			};
			momentumAnimationRef.current = requestAnimationFrame(step);
		}
	}
	setIsDragging(false);
	dragLastPositionRef.current = { x: 0, y: 0 };
	dragLastTimestampRef.current = 0;
};

	// Render expand/collapse buttons
	const renderExpandButtons = () => {
		return layoutData.nodes
			.filter((nodeInfo) => nodeInfo.hasChildren)
			.map((nodeInfo) => {
				// Use pre-calculated button position (below node, centered)
				const buttonPos = {
					x: nodeInfo.expandButtonX,
					y: nodeInfo.expandButtonY,
				};

				// Only apply animation when node is actually collapsing/disappearing
				const animation = nodeInfo.isCollapsing
					? 'mindMapFadeIn 0.2s ease-out reverse forwards'
					: undefined;

				const isCollapsed = nodeInfo.isExpanded === false;
				const buttonBorderColor = isCollapsed
					? colorToRgba(nodeAccentColor, 0.9, nodeAccentColor)
					: expandButtonStyle.border;
				const buttonBorderWidth = isCollapsed ? 1.6 : 0;

			return (
					<MindMapExpandCollapseButton
						key={`expand-${nodeInfo.renderKey}`}
						status={nodeInfo.isExpanded ? 'collapse' : 'expand'}
						size={expandButtonSize}
						position={buttonPos}
						backgroundColor={expandButtonStyle.background}
						hoverBackgroundColor={expandButtonStyle.hoverBackground}
						borderColor={buttonBorderColor}
						borderWidth={buttonBorderWidth}
						iconColor={expandButtonStyle.icon}
						focusRingColor={expandButtonStyle.focusRing}
						onClick={() => toggleExpanded(nodeInfo.renderKey)}
						style={{ ...(animation ? { animation } : {}) }}
					/>
				);
			});
	};

	// Render all traits in a single SVG container
	const renderTraits = () => {
		if (!filteredLayoutData.traits || filteredLayoutData.traits.length === 0) {
			return null;
		}

		// Calculate bounding box for all traits
		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;

		filteredLayoutData.traits.forEach((trait) => {
			minX = Math.min(minX, trait.start.x, trait.end.x);
			minY = Math.min(minY, trait.start.y, trait.end.y);
			maxX = Math.max(maxX, trait.start.x, trait.end.x);
			maxY = Math.max(maxY, trait.start.y, trait.end.y);
		});

		// Add padding
		const padding = 100;
		const svgX = Math.max(0, minX - padding);
		const svgY = Math.max(0, minY - padding);
		const svgWidth = maxX - minX + padding * 2;
		const svgHeight = maxY - minY + padding * 2;

		// Create individual path elements for each trait
		const paths = filteredLayoutData.traits.map((trait) => {
			// Build smooth curve path with subtle curves
			const x1 = trait.start.x;
			const y1 = trait.start.y;
			const x2 = trait.end.x;
			const y2 = trait.end.y;

			const distanceX = Math.abs(x2 - x1);
			const distanceY = Math.abs(y2 - y1);
			
			// Subtle curves like NotebookLM - minimal curvature for clean look
			// Horizontal offset creates gentle arc, vertical is minimal
			const horizontalOffset = distanceX * 0.15;
			const verticalOffset = distanceY * 0.08;

			// Control points create natural, subtle flowing curves
			const c1x = x1 + horizontalOffset;
			const c1y = y1 + verticalOffset;

			const c2x = x2 - horizontalOffset;
			const c2y = y2 - verticalOffset;

			const pathD = `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;

			const animation = trait.isCollapsing
			? 'mindMapTraitFadeIn 0.2s ease-out reverse forwards'
			: 'mindMapTraitFadeIn 0.2s ease-out';

			return (
				<path
					key={trait.id}
					d={pathD}
					fill="none"
					stroke={resolvedTraitColor}
					strokeWidth={traitStrokeWidth * 2}
					opacity={traitOpacity}
					strokeLinecap="round"
					strokeLinejoin="round"
					style={{ 
						animation,
						transition: 'd 0.2s ease-out, stroke 0.2s ease-out',
					}}
				/>
			);
		});

		return (
			<svg
				style={{
					position: 'absolute',
					left: svgX,
					top: svgY,
					width: svgWidth,
					height: svgHeight,
					overflow: 'visible',
					pointerEvents: 'none',
					zIndex: 0,
				}}
				viewBox={`${svgX} ${svgY} ${svgWidth} ${svgHeight}`}
				preserveAspectRatio="xMidYMid meet"
			>
				{paths}
			</svg>
		);
	};

	return (
		<>
			<style>
				{`
					@keyframes mindMapFadeIn {
						from {
							opacity: 0;
							transform: translateX(-8px);
						}
						to {
							opacity: 1;
							transform: translateX(0);
						}
					}

					@keyframes mindMapTraitFadeIn {
						from {
							opacity: 0;
							stroke-width: 0;
						}
						to {
							opacity: 1;
							stroke-width: ${traitStrokeWidth * 2};
						}
					}

					@keyframes mindMapTraitReposition {
						from {
							filter: blur(0.5px);
						}
						to {
							filter: blur(0px);
						}
					}
				`}
			</style>
			{isFullscreen && (
				<div
					aria-hidden="true"
					onClick={() => setIsFullscreen(false)}
					style={{
						position: 'fixed',
						inset: 0,
						zIndex: 9999,
						background: 'rgba(5, 7, 12, 0.72)',
						opacity: 1,
						pointerEvents: 'auto',
						transition: 'opacity 220ms ease',
					}}
				/>
			)}
			<div
				ref={containerRef}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onTouchCancel={handleTouchEnd}
				style={{
					position: isFullscreen ? 'fixed' : 'relative',
					inset: isFullscreen ? '3dvh auto auto 50%' : 'auto',
					transform: isFullscreen ? 'translateX(-50%)' : 'none',
					width: isFullscreen ? 'min(96vw, 1520px)' : containerWidth,
					height: isFullscreen ? '94dvh' : containerHeight,
					background: premiumCanvasBackground,
					border: 'none',
					boxShadow: 'none',
					borderRadius: 16,
					overflow: 'hidden',
					padding: 'clamp(12px, 3vw, 40px)',
					touchAction: 'none',
					display: 'block',
					cursor: isDragging ? 'grabbing' : 'grab',
					userSelect: 'none',
					zIndex: isFullscreen ? 10000 : 1,
					transition: 'all 0.25s ease',
				}}
			>
				<FullscreenToggleButton
					isFullscreen={isFullscreen}
					onToggle={toggleFullscreen}
					color={resolvedFullscreenButtonColor}
					size={fullscreenButtonSize}
					zIndex={10001}
				/>
				<PlusButton
					onClick={handleZoomIn}
					color={resolvedZoomButtonColor}
					size={zoomButtonSize}
					style={{
						position: 'absolute',
						bottom: 62,
						right: 14,
						zIndex: 10001,
					}}
				/>
				<CenterMapButton
					onClick={handleCenterMap}
					color={resolvedZoomButtonColor}
					size={zoomButtonSize}
					style={{
						position: 'absolute',
						bottom: 110,
						right: 14,
						zIndex: 10001,
					}}
				/>
				<MinusButton
					onClick={handleZoomOut}
					color={resolvedZoomButtonColor}
					size={zoomButtonSize}
					style={{
						position: 'absolute',
						bottom: 14,
						right: 14,
						zIndex: 10001,
					}}
				/>
				<div style={{ 
					position: 'relative', 
					minWidth: '100%',
					transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
					transformOrigin: '0 0',
					transition: (isDragging || isMomentumPanning) ? 'none' : 'transform 0.1s ease-out',
				}}>
				{/* Render all traits first (so they appear behind nodes) */}
				{renderTraits()}
				
				{/* Render all nodes */}
				{renderNodes()}
				
					{/* Render expand/collapse buttons */}
					{renderExpandButtons()}
				</div>
			</div>
		</>
	);
};

export default MindMap;
